import type { ApiResponse } from "@jaiho/shared";

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? "";

export class ApiError extends Error {
  constructor(
    public readonly code: string,
    message: string,
    public readonly status: number
  ) {
    super(message);
    this.name = "ApiError";
  }
}

let sessionToken: string | null = null;

export function setSessionToken(token: string | null): void {
  sessionToken = token;
}

export function getSessionToken(): string | null {
  return sessionToken;
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const headers = new Headers(init?.headers);
  headers.set("Content-Type", "application/json");
  if (sessionToken) {
    headers.set("Authorization", `Bearer ${sessionToken}`);
  }

  const res = await fetch(`${API_BASE_URL}${path}`, { ...init, headers });
  const body = (await res.json()) as ApiResponse<T>;

  if (!body.ok) {
    throw new ApiError(body.error.code, body.error.message, res.status);
  }

  return body.data;
}

export const api = {
  get: <T>(path: string) => request<T>(path, { method: "GET" }),
  post: <T>(path: string, data?: unknown) =>
    request<T>(path, { method: "POST", body: data === undefined ? undefined : JSON.stringify(data) }),
};

/**
 * Builds a WebSocket URL. Deliberately does NOT attach the normal session
 * token — a long-lived bearer token has no business sitting in a WS URL
 * (browser history, proxy/server access logs). Callers pass a short-lived
 * `ticket` from POST /api/v1/chat/ws-ticket instead.
 */
export function wsUrl(path: string, params: Record<string, string> = {}): string {
  const base = API_BASE_URL || window.location.origin;
  const url = new URL(base + path);
  url.protocol = url.protocol === "https:" ? "wss:" : "ws:";
  for (const [key, value] of Object.entries(params)) {
    url.searchParams.set(key, value);
  }
  return url.toString();
}
